import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import auth from "../../services/auth";
import { usePublicBrand } from "../../context/BrandContext";

/* ── Logo zone — logo only, no name text (matches Login/Register) ── */
function LogoZone({ publicBrand }) {
  const [imgErr, setImgErr] = useState(false);
  const src = publicBrand?.logoUrl || "/assets/project_logo.png";
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        marginBottom: 28,
        paddingBottom: 24,
        borderBottom: "1px solid #edf0f4",
      }}
    >
      {imgErr ? (
        <div
          style={{
            width: 64,
            height: 64,
            borderRadius: 14,
            background: "#f0f2f5",
            border: "1px solid #e5e7eb",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <span
            style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: "1rem",
              color: "#111111",
              letterSpacing: "0.04em",
            }}
          >
            MDP
          </span>
        </div>
      ) : (
        <img
          src={src}
          alt="Logo"
          style={{ height: 64, maxWidth: 180, objectFit: "contain" }}
          onError={() => setImgErr(true)}
        />
      )}
    </div>
  );
}

export default function VerifyOtpPage() {
  const [sp] = useSearchParams();
  const email = sp.get("email") || "";
  const nav = useNavigate();
  const publicBrand = usePublicBrand();

  const [digits, setDigits] = useState(Array(6).fill(""));
  const [msg, setMsg] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [done, setDone] = useState(false);
  const inputRefs = useRef([]);

  useEffect(() => {
    if (!email)
      setMsg({ type: "error", text: "Missing email. Please register again." });
  }, [email]);

  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  useEffect(() => {
    if (countdown <= 0) return;
    const t = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [countdown]);

  function handleChange(i, val) {
    const next = [...digits];
    next[i] = val.replace(/[^\d]/g, "").slice(-1);
    setDigits(next);
    if (next[i] && i < 5) inputRefs.current[i + 1]?.focus();
  }

  function handleKeyDown(i, e) {
    if (e.key === "Backspace" && !digits[i] && i > 0)
      inputRefs.current[i - 1]?.focus();
  }

  function handlePaste(e) {
    const t = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    const next = Array(6).fill("");
    t.split("").forEach((c, i) => {
      if (i < 6) next[i] = c;
    });
    setDigits(next);
    inputRefs.current[Math.min(t.length - 1, 5)]?.focus();
  }

  async function onVerify() {
    const code = digits.join("");
    if (code.length !== 6) {
      setMsg({ type: "error", text: "Please enter all 6 digits." });
      return;
    }
    setMsg(null);
    setIsLoading(true);
    try {
      await auth.verifyOtp(email, code);
      setDone(true);
      setTimeout(() => nav("/login"), 1400);
    } catch (e) {
      setMsg({
        type: "error",
        text: e?.data?.message || "Invalid code. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  }

  async function onResend() {
    if (countdown > 0) return;
    setIsResending(true);
    setMsg(null);
    try {
      await auth.resendOtp(email);
      setMsg({ type: "success", text: "New code sent to your email!" });
      setCountdown(60);
      setDigits(Array(6).fill(""));
      inputRefs.current[0]?.focus();
    } catch (e) {
      setMsg({
        type: "error",
        text: e?.data?.message || "Error resending code.",
      });
    } finally {
      setIsResending(false);
    }
  }

  const isComplete = digits.every((d) => d !== "");

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#f5f6f8",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "40px 20px",
        fontFamily: "'DM Sans', sans-serif",
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 460,
          background: "#ffffff",
          border: "1px solid #e0e5ee",
          borderRadius: 18,
          padding: "40px 40px",
          boxShadow: "0 4px 32px rgba(0,0,0,0.07)",
        }}
      >
        {/* Logo — only the image, no name text */}
        <LogoZone publicBrand={publicBrand} />

        {!done ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
            }}
          >
            {/* Icon */}
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 60,
                height: 60,
                borderRadius: "50%",
                background: "rgba(0,0,0,0.06)",
                border: "1px solid rgba(0,0,0,0.12)",
                marginBottom: 18,
              }}
            >
              <Mail24 />
            </div>

            <div
              style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: "0.6rem",
                letterSpacing: "0.04em",
                color: "#9ca3af",
                marginBottom: 6,
              }}
            >
              Email verification
            </div>
            <h2
              style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: "2.2rem",
                lineHeight: 1.1,
                color: "#111827",
                margin: "0 0 10px",
              }}
            >
              Check Your Inbox
            </h2>
            <p
              style={{
                fontFamily: "'DM Sans'",
                fontSize: "0.875rem",
                color: "#6b7280",
                lineHeight: 1.65,
                marginBottom: 24,
                fontWeight: 300,
              }}
            >
              We sent a 6-digit code to
              <br />
              <strong style={{ color: "#374151" }}>
                {email || "your email address"}
              </strong>
            </p>

            {/* Message */}
            {msg && (
              <div
                style={{
                  width: "100%",
                  marginBottom: 16,
                  padding: "10px 14px",
                  borderRadius: 10,
                  fontFamily: "'DM Sans'",
                  fontSize: "0.82rem",
                  background:
                    msg.type === "success"
                      ? "rgba(34,197,94,0.05)"
                      : "rgba(239,68,68,0.05)",
                  border: `1px solid ${msg.type === "success" ? "rgba(34,197,94,0.25)" : "rgba(239,68,68,0.25)"}`,
                  color: msg.type === "success" ? "#16a34a" : "#dc2626",
                }}
              >
                {msg.text}
              </div>
            )}

            {/* Digit inputs */}
            <div
              style={{
                display: "flex",
                gap: 10,
                justifyContent: "center",
                marginBottom: 24,
                width: "100%",
              }}
              onPaste={handlePaste}
            >
              {digits.map((d, i) => (
                <input
                  key={i}
                  ref={(el) => (inputRefs.current[i] = el)}
                  value={d}
                  maxLength={1}
                  inputMode="numeric"
                  onChange={(e) => handleChange(i, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(i, e)}
                  style={{
                    flex: 1,
                    maxWidth: 58,
                    height: 62,
                    textAlign: "center",
                    fontFamily: "'DM Sans', sans-serif",
                    fontWeight: 700,
                    fontSize: "1.5rem",
                    borderRadius: 10,
                    border: `1.5px solid ${d ? "#111111" : "#dde1e9"}`,
                    background: d ? "#f0f0f0" : "#f8f9fb",
                    color: "#111827",
                    outline: "none",
                    boxShadow: d ? "0 0 0 3px rgba(0,0,0,0.10)" : "none",
                    transition: "all 0.15s",
                    cursor: "text",
                  }}
                />
              ))}
            </div>

            {/* Verify button */}
            <button
              type="button"
              onClick={onVerify}
              disabled={!isComplete || isLoading}
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 7,
                width: "100%",
                padding: "13px 22px",
                background: !isComplete || isLoading ? "#e5e7eb" : "#111111",
                color: !isComplete || isLoading ? "#9ca3af" : "#ffffff",
                border: "none",
                borderRadius: 10,
                fontFamily: "'DM Sans'",
                fontWeight: 600,
                fontSize: "0.9rem",
                cursor: !isComplete || isLoading ? "not-allowed" : "pointer",
                boxShadow: "none",
                marginBottom: 16,
              }}
            >
              {isLoading ? (
                <>
                  <SpinnerInline /> Verifying…
                </>
              ) : (
                "Verify Code"
              )}
            </button>

            {/* Resend */}
            <div
              style={{
                fontFamily: "'DM Sans'",
                fontSize: "0.82rem",
                color: "#9ca3af",
              }}
            >
              Didn't receive it?{" "}
              <button
                type="button"
                onClick={onResend}
                disabled={countdown > 0 || isResending}
                style={{
                  background: "none",
                  border: "none",
                  cursor: countdown > 0 ? "default" : "pointer",
                  fontFamily: "'DM Sans'",
                  fontSize: "0.82rem",
                  color: "#111111",
                  fontWeight: 500,
                  opacity: countdown > 0 ? 0.5 : 1,
                }}
              >
                {isResending
                  ? "Sending…"
                  : countdown > 0
                    ? `Resend in ${countdown}s`
                    : "Resend code"}
              </button>
            </div>

            <div
              style={{
                marginTop: 20,
                paddingTop: 16,
                borderTop: "1px solid #edf0f4",
                width: "100%",
              }}
            >
              <button
                type="button"
                onClick={() => nav("/register")}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  fontFamily: "'DM Sans'",
                  fontSize: "0.78rem",
                  color: "#9ca3af",
                }}
              >
                Wrong email? Register again
              </button>
            </div>

            <p
              style={{
                marginTop: 8,
                fontFamily: "'DM Sans', sans-serif",
                fontSize: "0.65rem",
                color: "#d1d5db",
              }}
            >
              For your security, this code expires in 10 minutes.
            </p>
          </div>
        ) : (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
            }}
          >
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 60,
                height: 60,
                borderRadius: "50%",
                background: "rgba(34,197,94,0.08)",
                border: "1px solid rgba(34,197,94,0.2)",
                marginBottom: 18,
              }}
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#16a34a"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10" />
                <path d="m9 12 2 2 4-4" />
              </svg>
            </div>
            <div
              style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: "0.6rem",
                letterSpacing: "0.04em",
                color: "#9ca3af",
                marginBottom: 6,
              }}
            >
              Verified
            </div>
            <h2
              style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: "2.2rem",
                lineHeight: 1.1,
                color: "#111827",
                margin: "0 0 10px",
              }}
            >
              Account Activated!
            </h2>
            <p
              style={{
                fontFamily: "'DM Sans'",
                fontSize: "0.875rem",
                color: "#6b7280",
                lineHeight: 1.65,
                margin: 0,
                fontWeight: 300,
              }}
            >
              Your account is active. Redirecting to sign in…
            </p>
          </div>
        )}
      </div>

      {/* T&C + Privacy Policy — below the card */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 6,
          marginTop: 20,
          flexWrap: "wrap",
        }}
      >
        <Link
          to={`${process.env.REACT_APP_LANDING_URL}/terms`}
          style={{
            fontFamily: "'DM Sans'",
            fontSize: "0.75rem",
            color: "#9ca3af",
            textDecoration: "none",
            transition: "color 0.15s",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.color = "#6b7280")}
          onMouseLeave={(e) => (e.currentTarget.style.color = "#9ca3af")}
        >
          Terms &amp; Conditions
        </Link>
        <span style={{ color: "#d1d5db", fontSize: "0.75rem" }}>·</span>
        <Link
          to={`${process.env.REACT_APP_LANDING_URL}/privacy`}
          style={{
            fontFamily: "'DM Sans'",
            fontSize: "0.75rem",
            color: "#9ca3af",
            textDecoration: "none",
            transition: "color 0.15s",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.color = "#6b7280")}
          onMouseLeave={(e) => (e.currentTarget.style.color = "#9ca3af")}
        >
          Privacy Policy
        </Link>
      </div>
    </div>
  );
}

/* ── Inline icons ─────────────────────────────────────────────────── */
function Mail24() {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#111111"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="2" y="4" width="20" height="16" rx="2" />
      <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
    </svg>
  );
}

function SpinnerInline() {
  return (
    <span
      style={{
        display: "inline-block",
        width: 13,
        height: 13,
        borderRadius: "50%",
        border: "2px solid rgba(0,0,0,0.15)",
        borderTopColor: "#111827",
        animation: "auth-spin 0.75s linear infinite",
        flexShrink: 0,
      }}
    />
  );
}
